import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { 
  Users, BarChart3, Shield, Zap, Target, Award, ArrowRight, Phone, 
  CheckCircle, School, TrendingUp, Heart, Clock, Star, Play, ChevronDown,
  Calendar, IndianRupee, BookOpen, MessageSquare, PieChart, Globe,
  Building, GraduationCap, FileText, Lightbulb, Check
} from 'lucide-react';
import Footer from '../components/Footer';
import Navigation from '../components/Navigation';

const PrincipalLanding = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [showDemoForm, setShowDemoForm] = useState(false);
  const [demoForm, setDemoForm] = useState({ name: '', email: '', phone: '', school: '', students: '' });
  const [roi, setRoi] = useState({ students: 100, fee: 3000 });

  // ROI Calculator
  const monthlyRevenue = roi.students * roi.fee * 0.7; // 70% margin
  const annualRevenue = monthlyRevenue * 12;

  const stats = [
    { value: '50+', label: 'Partner Schools', icon: School },
    { value: '5000+', label: 'Students Enrolled', icon: Users },
    { value: '92%', label: 'Parent Satisfaction', icon: Heart },
    { value: '35%', label: 'Revenue Growth', icon: TrendingUp }
  ];

  const features = [
    { 
      icon: Users, 
      title: 'Managed Observer Team', 
      desc: 'We recruit, train, and manage observers. You just oversee quality.',
      highlight: 'Zero hiring hassle'
    },
    { 
      icon: BarChart3, 
      title: 'AI-Powered Insights', 
      desc: 'Advanced analytics identify patterns and early warning signs automatically.',
      highlight: 'Real-time dashboards'
    },
    { 
      icon: Shield, 
      title: 'Complete Privacy', 
      desc: 'DPDP-compliant platform with end-to-end encryption.',
      highlight: 'Data protection guaranteed'
    },
    { 
      icon: IndianRupee, 
      title: 'New Revenue Stream', 
      desc: 'Offer premium emotional wellness service to parents.',
      highlight: 'High margin service'
    },
    { 
      icon: Target, 
      title: 'Parent Engagement', 
      desc: 'Weekly reports and insights strengthen school-parent relationship.',
      highlight: 'Increased retention'
    },
    { 
      icon: Award, 
      title: 'Differentiation', 
      desc: 'Stand out from competitors with cutting-edge child development focus.',
      highlight: 'Market leadership'
    }
  ];

  const timeline = [
    { week: 'Week 1', title: 'Partnership Setup', items: ['Contract signing', 'School onboarding', 'System configuration'] },
    { week: 'Week 2', title: 'Observer Assignment', items: ['Observer recruitment', 'Training completion', 'Quality verification'] },
    { week: 'Week 3', title: 'Parent Enrollment', items: ['Marketing materials', 'Parent orientation', 'Trial sessions'] },
    { week: 'Week 4', title: 'Full Launch', items: ['Regular sessions begin', 'Dashboard access', 'Support activation'] }
  ];

  const caseStudies = [
    {
      school: 'Greenwood International School',
      location: 'Mumbai',
      students: 450,
      results: [
        { metric: 'Parent Satisfaction', before: '72%', after: '94%' },
        { metric: 'Student Confidence', before: '65%', after: '88%' },
        { metric: 'New Revenue', before: '₹0', after: '₹12L/year' }
      ],
      quote: 'Sanjaya transformed how we support our students. Parents now see us as partners in their child\'s complete development.',
      principal: 'Dr. Meera Sharma',
      logo: 'G'
    },
    {
      school: 'Bright Future Academy',
      location: 'Delhi',
      students: 280,
      results: [
        { metric: 'Early Issue Detection', before: '25%', after: '78%' },
        { metric: 'Parent Retention', before: '82%', after: '96%' },
        { metric: 'New Revenue', before: '₹0', after: '₹8L/year' }
      ],
      quote: 'The AI insights help us identify concerns before they become problems. It\'s like having an extra team member for every child.',
      principal: 'Mrs. Anita Kapoor',
      logo: 'B'
    }
  ];

  const pricingModels = [
    {
      name: 'Revenue Share',
      desc: 'No upfront cost',
      features: ['Zero investment required', 'We handle everything', 'You earn 40% of parent fees', 'Ideal for getting started'],
      cta: 'Most Popular',
      highlight: true
    },
    {
      name: 'White Label',
      desc: 'Your brand, our platform',
      features: ['Branded as your program', 'Higher margins (60%)', 'Dedicated support', 'Custom features available'],
      cta: 'Premium',
      highlight: false
    },
    {
      name: 'Enterprise',
      desc: 'Large institutions',
      features: ['Custom pricing', 'Multi-campus support', 'API integrations', 'Dedicated account manager'],
      cta: 'Contact Us',
      highlight: false
    }
  ];

  const faqs = [
    {
      q: 'What investment is required to start?',
      a: 'With our Revenue Share model, there\'s zero upfront investment. We handle all setup, training, and operations. You simply promote the service to parents and earn 40% of all fees collected.'
    },
    {
      q: 'How do you ensure quality of observers?',
      a: 'All observers go through rigorous screening, background checks, and our comprehensive training program. They\'re continuously monitored through session audits and parent feedback. You have full visibility into quality metrics.'
    },
    {
      q: 'What if parents don\'t sign up?',
      a: 'We provide marketing materials, parent presentation templates, and support for parent orientation sessions. On average, 30-40% of parents enroll in the first year, growing to 60%+ in subsequent years.'
    },
    {
      q: 'How is student data protected?',
      a: 'We\'re fully DPDP compliant with end-to-end encryption, secure data centers in India, and strict access controls. Parents own their data and can delete it anytime. We never sell or share data.'
    },
    {
      q: 'Can we customize the program for our school?',
      a: 'Yes! White Label partners can customize branding, reports, and even session focus areas. We work with you to align the program with your school\'s values and curriculum.'
    },
    {
      q: 'What support do you provide?',
      a: 'You get a dedicated account manager, 24/7 technical support, monthly business reviews, and access to our principal community. We\'re invested in your success.'
    }
  ];

  const handleDemoSubmit = (e) => {
    e.preventDefault();
    // Handle demo request
    alert('Thank you! We\'ll contact you within 24 hours.');
    setShowDemoForm(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIzMCIgY3k9IjMwIiByPSIyIi8+PC9nPjwvZz48L3N2Zz4=')] opacity-30"></div>
        
        <div className="max-w-7xl mx-auto px-4 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full text-sm mb-6">
                <School className="w-4 h-4" /> Trusted by 50+ Schools
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Add ₹10-50 Lakhs<br/>
                <span className="text-blue-200">Annual Revenue</span>
              </h1>
              <p className="text-xl text-blue-100 mb-8">
                Partner with Sanjaya to offer AI-powered emotional wellness for students. 
                Zero investment, new revenue stream, happier parents.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  size="lg" 
                  onClick={() => setShowDemoForm(true)}
                  className="bg-white text-blue-700 hover:bg-blue-50 text-lg px-8 py-6"
                >
                  Schedule Free Demo <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => navigate('/principal/login')}
                  className="border-white text-white hover:bg-white/10 text-lg px-8 py-6"
                >
                  Partner Login
                </Button>
              </div>
              <div className="flex items-center gap-6 mt-8 text-blue-100 text-sm">
                <span className="flex items-center gap-1"><Check className="w-4 h-4" /> Zero investment</span>
                <span className="flex items-center gap-1"><Check className="w-4 h-4" /> 4-week launch</span>
                <span className="flex items-center gap-1"><Check className="w-4 h-4" /> Full support</span>
              </div>
            </div>
            
            {/* ROI Calculator */}
            <Card className="bg-white/10 backdrop-blur-lg border-white/20">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <PieChart className="w-5 h-5" /> Revenue Calculator
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-blue-200 text-sm">Students in your school</label>
                    <Input
                      type="number"
                      value={roi.students}
                      onChange={(e) => setRoi({...roi, students: parseInt(e.target.value) || 0})}
                      className="mt-1 bg-white/20 border-white/30 text-white placeholder:text-white/50"
                    />
                  </div>
                  <div>
                    <label className="text-blue-200 text-sm">Monthly fee per student (₹)</label>
                    <div className="flex gap-2 mt-1">
                      {[2000, 3000, 4000, 5000].map(f => (
                        <button
                          key={f}
                          onClick={() => setRoi({...roi, fee: f})}
                          className={`flex-1 py-2 rounded-lg font-medium transition-all text-sm ${
                            roi.fee === f 
                              ? 'bg-white text-blue-700' 
                              : 'bg-white/20 hover:bg-white/30'
                          }`}
                        >
                          ₹{f}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="pt-4 border-t border-white/20">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-blue-200 text-sm">Estimated enrollments (30%)</p>
                        <p className="text-2xl font-bold">{Math.round(roi.students * 0.3)}</p>
                      </div>
                      <div>
                        <p className="text-blue-200 text-sm">Your share (40%)</p>
                        <p className="text-2xl font-bold">₹{(roi.fee * 0.4).toLocaleString()}/student</p>
                      </div>
                    </div>
                    <div className="mt-4 p-4 bg-white/20 rounded-lg">
                      <p className="text-blue-200 text-sm">Potential Annual Revenue</p>
                      <p className="text-4xl font-bold text-white">
                        ₹{(Math.round(roi.students * 0.3) * roi.fee * 0.4 * 12).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-8 bg-gray-50 border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <stat.icon className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-gray-600 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Schools Partner With Sanjaya
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              A complete solution that benefits students, parents, and your institution
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-shadow border-t-4 border-t-blue-500">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{feature.title}</h3>
                      <p className="text-gray-600 text-sm mb-2">{feature.desc}</p>
                      <span className="text-blue-600 text-xs font-medium bg-blue-50 px-2 py-1 rounded">
                        {feature.highlight}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-16 px-4 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Success Stories
            </h2>
            <p className="text-xl text-gray-600">
              See how other schools transformed with Sanjaya
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {caseStudies.map((cs, idx) => (
              <Card key={idx} className="overflow-hidden">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-4 text-white">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center text-2xl font-bold">
                      {cs.logo}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">{cs.school}</h3>
                      <p className="text-blue-100">{cs.location} • {cs.students} students</p>
                    </div>
                  </div>
                </div>
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                    {cs.results.map((r, ridx) => (
                      <div key={ridx} className="text-center p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500">{r.metric}</p>
                        <div className="flex items-center justify-center gap-2 mt-1">
                          <span className="text-gray-400 text-sm line-through">{r.before}</span>
                          <ArrowRight className="w-3 h-3 text-green-500" />
                          <span className="text-green-600 font-bold">{r.after}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <blockquote className="border-l-4 border-blue-500 pl-4 italic text-gray-600 mb-4">
                    "{cs.quote}"
                  </blockquote>
                  <p className="text-gray-900 font-medium">— {cs.principal}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Implementation Timeline */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Launch in 4 Weeks
            </h2>
            <p className="text-xl text-gray-600">
              Our proven implementation process
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6">
            {timeline.map((phase, idx) => (
              <Card key={idx} className={`${idx === 3 ? 'border-2 border-green-500' : ''}`}>
                <CardContent className="p-6">
                  <div className="text-blue-600 font-bold text-sm mb-2">{phase.week}</div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">{phase.title}</h3>
                  <ul className="space-y-2">
                    {phase.items.map((item, iidx) => (
                      <li key={iidx} className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Models */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Partnership Models
            </h2>
            <p className="text-xl text-gray-600">
              Flexible options to fit your institution
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {pricingModels.map((model, idx) => (
              <Card key={idx} className={`${model.highlight ? 'border-2 border-blue-500 shadow-lg scale-105' : ''}`}>
                {model.highlight && (
                  <div className="bg-blue-500 text-white text-center py-2 text-sm font-medium">
                    {model.cta}
                  </div>
                )}
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900">{model.name}</h3>
                  <p className="text-gray-600 mb-4">{model.desc}</p>
                  <ul className="space-y-3 mb-6">
                    {model.features.map((f, fidx) => (
                      <li key={fidx} className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-500" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className={`w-full ${model.highlight ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                    variant={model.highlight ? 'default' : 'outline'}
                    onClick={() => setShowDemoForm(true)}
                  >
                    Get Started
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <Card key={idx} className="overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full p-4 text-left flex items-center justify-between hover:bg-gray-50"
                >
                  <span className="font-medium text-gray-900">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${openFaq === idx ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === idx && (
                  <div className="px-4 pb-4">
                    <p className="text-gray-600">{faq.a}</p>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Transform Your School?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join 50+ schools already partnering with Sanjaya. 
            Schedule a free demo and see the platform in action.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              size="lg" 
              onClick={() => setShowDemoForm(true)}
              className="bg-white text-blue-700 hover:bg-blue-50 text-lg px-8 py-6"
            >
              Schedule Free Demo <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="border-white text-white hover:bg-white/10 text-lg px-8 py-6"
              onClick={() => window.location.href = 'tel:+918291188188'}
            >
              <Phone className="w-5 h-5 mr-2" /> Call +91 8291 188 188
            </Button>
          </div>
        </div>
      </section>

      {/* Demo Request Modal */}
      {showDemoForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardContent className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Schedule Your Free Demo</h3>
              <form onSubmit={handleDemoSubmit} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Your Name *</label>
                  <Input
                    required
                    value={demoForm.name}
                    onChange={(e) => setDemoForm({...demoForm, name: e.target.value})}
                    placeholder="Dr. / Mr. / Mrs."
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Email *</label>
                  <Input
                    type="email"
                    required
                    value={demoForm.email}
                    onChange={(e) => setDemoForm({...demoForm, email: e.target.value})}
                    placeholder="principal@school.edu"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Phone *</label>
                  <Input
                    required
                    value={demoForm.phone}
                    onChange={(e) => setDemoForm({...demoForm, phone: e.target.value})}
                    placeholder="+91 98765 43210"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">School Name *</label>
                  <Input
                    required
                    value={demoForm.school}
                    onChange={(e) => setDemoForm({...demoForm, school: e.target.value})}
                    placeholder="Your School Name"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Number of Students</label>
                  <Input
                    type="number"
                    value={demoForm.students}
                    onChange={(e) => setDemoForm({...demoForm, students: e.target.value})}
                    placeholder="e.g., 500"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowDemoForm(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
                    Request Demo
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default PrincipalLanding;
